'use client';

import { useMemo, useRef } from 'react';
import { SpotBalanceType } from '@velocity-exchange/sdk';
import { matchEnum } from '@velocity-exchange/common';
import useVelocityAccountStore, {
	AccountSpotBalance,
	checkIfAccountsSpotBalancesAreEqual,
} from 'src/stores/useVelocityAccountsStore';
import useUiUpdateInterval from './useUiUpdateInterval';

const EMPTY_BALANCES: AccountSpotBalance[] = [];

const useAccountSpotBalances = (
	userKey: string | undefined,
	balanceType?: SpotBalanceType | null,
	poolId?: number
): AccountSpotBalance[] => {
	const userAccountBalances = useVelocityAccountStore((s) =>
		userKey
			? (s.accounts[userKey]?.spotBalances ?? EMPTY_BALANCES)
			: EMPTY_BALANCES
	);

	return useMemo(() => {
		return userAccountBalances
			.filter((balance: AccountSpotBalance) =>
				balanceType ? matchEnum(balance.balanceType, balanceType) : true
			)
			.filter((balance: AccountSpotBalance) =>
				poolId !== undefined ? balance.asset.poolId === poolId : true
			);
	}, [userAccountBalances, balanceType, poolId]);
};

export const useAggregateAccountsSpotBalances = (
	balanceType?: SpotBalanceType
) => {
	const getAggregateAccountsSpotBalances = useVelocityAccountStore(
		(s) => s.getAggregateAccountsSpotBalances
	);

	const aggregateAccountBalancesRef = useRef<AccountSpotBalance[]>([]);

	const getAndSetAggregateAccountBalances = () => {
		const newAggregateAccountsSpotBalances = getAggregateAccountsSpotBalances();
		const filteredBalances = newAggregateAccountsSpotBalances.filter(
			(balance) =>
				balanceType ? matchEnum(balance.balanceType, balanceType) : true
		);

		if (
			!checkIfAccountsSpotBalancesAreEqual(
				filteredBalances,
				aggregateAccountBalancesRef.current
			)
		) {
			aggregateAccountBalancesRef.current = filteredBalances;
		}
	};

	useUiUpdateInterval(getAndSetAggregateAccountBalances, true, false);

	return aggregateAccountBalancesRef.current;
};

export default useAccountSpotBalances;
